import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostBinding, HostListener, inject, Input, ViewChild, ViewEncapsulation } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'
import { DecodeHtmlStringPipe } from '../../pipes'
import { FieldComponent } from '../field/field.component'

/**
 * @public
 */
export type InputElement = NgElement & WithProperties<InputComponent>

/**
 * @public
 */
@Component({
  selector: 'gpei-input',
  templateUrl: './input.component.html',
  styleUrls: [ './input.component.sass' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [
    CommonModule,
    DecodeHtmlStringPipe
  ]
})
export class InputComponent extends FieldComponent implements AfterViewInit {

  static readonly DEFAULT_TYPE = 'text'
  static readonly DEFAULT_MAX_LENGTH = 300

  private _step = 'any'

  @ViewChild('input')
  private readonly _inputRef: ElementRef<HTMLInputElement>

  protected _elementRef = inject(ElementRef)

  @Input()
  @HostBinding()
  pattern: string

  @Input()
  @HostBinding()
  placeholder = ''

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('maxlength')
  @HostBinding()
  maxLength = InputComponent.DEFAULT_MAX_LENGTH

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('type')
  @HostBinding()
  inputType: string = InputComponent.DEFAULT_TYPE

  @Input()
  @HostBinding()
  size: string

  ngAfterViewInit(): void {
    this.syncValidity(this._inputRef?.nativeElement)
  }

  onInput(event: Event): void {
    // Pause propagation of input event
    event.stopPropagation()
    let target = (event as InputEvent).target as HTMLInputElement

    // Set the value
    this.writeValue(target.value)

    // Mark it as touched
    this.touched = true

    // Update the validity of the element
    this.syncValidity(target)

    // Detect changes
    this._changeDetectorRef.detectChanges()

    // Resume propagation of input event
    this._elementRef.nativeElement.dispatchEvent(new InputEvent('input', { ...event, bubbles: true }))
  }

  /**
   * Synchronize the validity of the element with the validity state of the provided element
   *
   * @param element The target element
   */
  private syncValidity(element: HTMLInputElement): void {
    this.setValidity(
      element?.validity,
      element?.validationMessage,
      element
    )
  }

  /**
   * Lifecycle hook for when hitting enter or requesting a form to submit from a child element.
   *
   * This will request any existing parent form be submitted when the enter key is pressed. This functionality
   * exists in native elements, and this adds that support for our custom elements as well.
   */
  @HostListener('keydown.enter')
  onSubmit(): void {
    this?.form?.requestSubmit()
  }

  @Input()
  @HostBinding()
  set value(value: string) {
    super.value = value
    this._changeDetectorRef.detectChanges()
    this.syncValidity(this._inputRef?.nativeElement)
  }


  get value(): string {
    return super.value
  }

  @Input()
  @HostBinding()
  set step(value: string) {
    this._step = value
  }

  get step(): string {
    return this.inputType.toLowerCase() === 'number' ? this._step : null
  }

}

/**
 * @public
 */
export const registerInputElement = registerCustomElementFactory(InputComponent)
